import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Node types that may appear as top-level blocks and therefore carry a stable `data-block-id`.
 * Types that are not registered in a given editor are ignored by TipTap.
 */
export const BLOCK_ID_TYPES = [
  'paragraph',
  'heading',
  'blockquote',
  'bulletList',
  'orderedList',
  'taskList',
  'codeBlock',
  'horizontalRule',
  'table',
  'image',
  'mermaidBlock',
  'trustSection',
  'templateFieldBlock',
  'generatedField',
];

export const blockIdPluginKey = new PluginKey('quillBlockId');

/** Meta value that forces a stamping pass even without a document change. */
export const BLOCK_ID_STAMP_META = 'stamp';

/**
 * Create a short random block id that is not in `taken`.
 * @param {Set<string>} taken - Ids already used in the document.
 * @returns {string} Id such as `b7k2m9qx`.
 */
export function createBlockId(taken) {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const id = `b${Math.random().toString(36).slice(2, 9).padEnd(7, '0')}`;
    if (!taken || !taken.has(id)) return id;
  }
  return `b${Date.now().toString(36)}`;
}

/**
 * Collect the ids of top-level blocks and return a transaction that assigns ids to blocks
 * that have none or share a duplicate id. Returns null when every block already has a unique id.
 * @param {import('@tiptap/pm/state').EditorState} state - Editor state.
 * @returns {import('@tiptap/pm/state').Transaction | null} Stamping transaction or null.
 */
export function buildBlockIdStampTransaction(state) {
  const seen = new Set();
  const missing = [];
  state.doc.forEach((node, offset) => {
    if (!Object.prototype.hasOwnProperty.call(node.attrs, 'blockId')) return;
    const id = node.attrs.blockId;
    if (!id || seen.has(id)) {
      missing.push(offset);
      return;
    }
    seen.add(id);
  });
  if (missing.length === 0) return null;
  const tr = state.tr;
  missing.forEach((offset) => {
    const id = createBlockId(seen);
    seen.add(id);
    tr.setNodeAttribute(offset, 'blockId', id);
  });
  return tr;
}

/**
 * Stamps a stable `data-block-id` on every top-level block so AI patch plans, the chat bridge
 * and "scroll to block" can address blocks without fragile HTML substring matching.
 * The attribute is kept in saved HTML (it is inert for exports and the worker) so ids survive
 * reloads. New blocks created by Enter do not inherit the id (keepOnSplit: false); duplicates
 * created by copy/paste are re-stamped. Stamping pauses while an AI review is active.
 */
const BlockId = Extension.create({
  name: 'blockId',

  addGlobalAttributes() {
    return [
      {
        types: BLOCK_ID_TYPES,
        attributes: {
          blockId: {
            default: null,
            keepOnSplit: false,
            parseHTML: (element) => element.getAttribute('data-block-id') || null,
            renderHTML: (attributes) => (attributes.blockId ? { 'data-block-id': attributes.blockId } : {}),
          },
        },
      },
    ];
  },

  onCreate() {
    const tr = buildBlockIdStampTransaction(this.editor.state);
    if (tr) {
      tr.setMeta('addToHistory', false);
      tr.setMeta('preventUpdate', true);
      this.editor.view.dispatch(tr);
    }
  },

  addProseMirrorPlugins() {
    const { editor } = this;
    return [
      new Plugin({
        key: blockIdPluginKey,
        appendTransaction: (transactions, oldState, newState) => {
          // While an AI review is shown, a replaced block and its replacement intentionally
          // share the original id (whichever survives keeps it). Ids are reconciled when the
          // review is finished (that transaction is dispatched after reviewActive is cleared).
          if (editor && editor.storage && editor.storage.quillRuntime && editor.storage.quillRuntime.reviewActive) return null;
          const relevant = transactions.some(
            (tr) => tr.docChanged || tr.getMeta(blockIdPluginKey) === BLOCK_ID_STAMP_META
          );
          if (!relevant) return null;
          const tr = buildBlockIdStampTransaction(newState);
          if (!tr) return null;
          const silent = transactions.every((t) => t.getMeta('addToHistory') === false);
          if (silent) tr.setMeta('addToHistory', false);
          if (transactions.every((t) => t.getMeta('preventUpdate'))) tr.setMeta('preventUpdate', true);
          return tr;
        },
      }),
    ];
  },
});

export default BlockId;
